/**
 * Administración de plantillas HTML, cuentas SMTP y pruebas de envío.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ink, studio, StudioTabs, SearchField, Field, PermSwitch, cols } from './studioUi';

const STARTER_HTML = `<!DOCTYPE html>
<html lang="es">
<body style="margin:0;padding:0;background:#f4f5f7;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f4f5f7;padding:28px 12px;">
    <tr><td align="center">
      <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#fff;border-radius:16px;overflow:hidden;border:1px solid #e8eaed;">
        <tr><td style="background:#2d0b57;padding:22px 28px;text-align:center;">
          <img src="{{logoUrl}}" alt="Central Media" width="168" style="max-width:168px;height:auto;border:0;">
        </td></tr>
        <tr><td style="height:4px;background:#5E35B1;font-size:0;">&nbsp;</td></tr>
        <tr><td style="padding:32px 36px;color:#1c1d21;">
          <p style="margin:0 0 8px;font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#5E35B1;">Portal de Reportes</p>
          <h1 style="margin:0 0 18px;font-size:22px;">Título del aviso</h1>
          <p style="margin:0 0 12px;font-size:15px;line-height:1.6;color:#3c4043;">Hola {{userName}},</p>
          <p style="margin:0;font-size:15px;line-height:1.6;color:#3c4043;">Escribe aquí el mensaje.</p>
          <table role="presentation" cellpadding="0" cellspacing="0" style="margin:28px 0 0;">
            <tr><td style="border-radius:10px;background:#5E35B1;">
              <a href="{{ctaUrl}}" style="display:inline-block;padding:12px 22px;color:#fff;text-decoration:none;font-weight:600;">Abrir portal</a>
            </td></tr>
          </table>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;

const fillVars = (template, vars) => String(template || '').replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => (
    vars[key] != null ? String(vars[key]) : `{{${key}}}`
));

const emptyAccount = {
    name: '',
    fromName: 'Portal de Reportes Central Media',
    fromEmail: '',
    host: '',
    port: 587,
    secure: false,
    user: '',
    password: '',
    makeDefault: false,
};

const EmailTemplatesPanel = ({ fetchWithAuth, API_URL, user }) => {
    const base = `${API_URL}/admin/email`;
    const [tab, setTab] = useState('templates');
    const [templates, setTemplates] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [settings, setSettings] = useState({ frontendUrl: 'https://reportes.centralmedia.mx', logoUrl: '', defaultAccountId: 'env' });
    const [query, setQuery] = useState('');
    const [category, setCategory] = useState('all');
    const [loading, setLoading] = useState(true);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState('');

    const [selectedId, setSelectedId] = useState(null);
    const [draft, setDraft] = useState(null);
    const [testTo, setTestTo] = useState(user?.email || '');

    const [accountForm, setAccountForm] = useState(emptyAccount);
    const [editingAccountId, setEditingAccountId] = useState(null);
    const [showAccountForm, setShowAccountForm] = useState(false);

    const api = useCallback(async (path, options = {}) => {
        const res = await fetchWithAuth(`${base}${path}`, {
            headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
            ...options,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.success === false) throw new Error(data.error || 'No se pudo completar la acción.');
        return data;
    }, [base, fetchWithAuth]);

    const showNotice = (type, text) => setNotice({ type, text });

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [tpl, acc, set] = await Promise.all([
                api('/templates'),
                api('/accounts'),
                api('/settings'),
            ]);
            setTemplates(tpl.templates || []);
            setAccounts(acc.accounts || []);
            setSettings(set.settings || settings);
        } catch (error) {
            showNotice('error', error.message);
        } finally {
            setLoading(false);
        }
    }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadAll(); }, [loadAll]);

    const categories = useMemo(
        () => ['all', ...Array.from(new Set(templates.map((item) => item.category).filter(Boolean)))],
        [templates]
    );

    const visible = useMemo(() => {
        const term = query.trim().toLowerCase();
        return templates.filter((item) => {
            if (category !== 'all' && item.category !== category) return false;
            if (!term) return true;
            return `${item.name} ${item.description} ${item.category}`.toLowerCase().includes(term);
        });
    }, [templates, query, category]);

    const grouped = useMemo(() => visible.reduce((acc, item) => {
        const key = item.category || 'General';
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
    }, {}), [visible]);

    const openTemplate = async (id) => {
        setBusy('open');
        try {
            const data = await api(`/templates/${id}`);
            setSelectedId(id);
            setDraft({
                name: data.template.name || '',
                subject: data.template.subject || '',
                html: data.template.html || '',
                enabled: data.template.enabled !== false,
                accountId: data.template.accountId || '',
                description: data.template.description || '',
                isSystem: data.template.isSystem !== false,
                placeholders: data.template.placeholders || [],
                sample: data.template.sample || {},
                category: data.template.category || 'Personalizada',
            });
        } catch (error) {
            showNotice('error', error.message);
        } finally {
            setBusy('');
        }
    };

    const createTemplate = async () => {
        setBusy('create');
        try {
            const data = await api('/templates', {
                method: 'POST',
                body: JSON.stringify({
                    name: 'Nueva plantilla',
                    subject: 'Aviso del Portal de Reportes',
                    html: STARTER_HTML,
                    category: 'Personalizada',
                    enabled: false,
                }),
            });
            await loadAll();
            await openTemplate(data.id);
            showNotice('ok', 'Plantilla creada. Edita el HTML y actívala cuando esté lista.');
        } catch (error) {
            showNotice('error', error.message);
        } finally {
            setBusy('');
        }
    };

    const saveTemplate = async () => {
        if (!selectedId || !draft) return;
        setBusy('save');
        try {
            await api(`/templates/${selectedId}`, {
                method: 'PUT',
                body: JSON.stringify({
                    name: draft.name,
                    subject: draft.subject,
                    html: draft.html,
                    enabled: draft.enabled,
                    accountId: draft.accountId || null,
                    description: draft.description,
                    category: draft.category,
                }),
            });
            await loadAll();
            showNotice('ok', 'Plantilla guardada.');
        } catch (error) {
            showNotice('error', error.message);
        } finally {
            setBusy('');
        }
    };

    const toggleEnabled = async (item, enabled) => {
        try {
            await api(`/templates/${item.id}`, { method: 'PUT', body: JSON.stringify({ enabled }) });
            setTemplates((prev) => prev.map((row) => (row.id === item.id ? { ...row, enabled } : row)));
            if (selectedId === item.id && draft) setDraft({ ...draft, enabled });
        } catch (error) {
            showNotice('error', error.message);
        }
    };

    const resetTemplate = async () => {
        if (!selectedId || !draft?.isSystem) return;
        if (!window.confirm('Esto vuelve al HTML y asunto originales. ¿Continuar?')) return;
        setBusy('reset');
        try {
            await api(`/templates/${selectedId}/reset`, { method: 'POST' });
            await loadAll();
            await openTemplate(selectedId);
            showNotice('ok', 'Plantilla restablecida.');
        } catch (error) {
            showNotice('error', error.message);
        } finally {
            setBusy('');
        }
    };

    const deleteTemplate = async () => {
        if (!selectedId || draft?.isSystem) return;
        if (!window.confirm('¿Eliminar esta plantilla personalizada?')) return;
        setBusy('delete');
        try {
            await api(`/templates/${selectedId}`, { method: 'DELETE' });
            setSelectedId(null);
            setDraft(null);
            await loadAll();
            showNotice('ok', 'Plantilla eliminada.');
        } catch (error) {
            showNotice('error', error.message);
        } finally {
            setBusy('');
        }
    };

    const sendTest = async () => {
        if (!selectedId || !testTo) return;
        setBusy('test');
        try {
            const data = await api(`/templates/${selectedId}/test`, {
                method: 'POST',
                body: JSON.stringify({
                    to: testTo,
                    subject: draft.subject,
                    html: draft.html,
                    accountId: draft.accountId || undefined,
                    vars: draft.sample,
                }),
            });
            if (data.result?.skipped) {
                showNotice('ok', 'SMTP no está configurado en este entorno: se simuló el envío.');
            } else {
                showNotice('ok', `Prueba enviada a ${testTo}.`);
            }
        } catch (error) {
            showNotice('error', error.message);
        } finally {
            setBusy('');
        }
    };

    const previewVars = useMemo(() => ({
        year: String(new Date().getFullYear()),
        portalUrl: settings.frontendUrl,
        logoUrl: settings.logoUrl,
        ctaUrl: settings.frontendUrl,
        ...(draft?.sample || {}),
    }), [settings, draft]);

    const livePreview = useMemo(() => {
        if (!draft) return { subject: '', html: '' };
        return {
            subject: fillVars(draft.subject, previewVars),
            html: fillVars(draft.html, previewVars),
        };
    }, [draft, previewVars]);

    const saveSettings = async (payload) => {
        const data = await api('/settings', { method: 'PUT', body: JSON.stringify(payload) });
        setSettings(data.settings);
    };

    const saveAccount = async (event) => {
        event.preventDefault();
        setBusy('account');
        try {
            if (editingAccountId) {
                await api(`/accounts/${editingAccountId}`, {
                    method: 'PUT',
                    body: JSON.stringify(accountForm),
                });
            } else {
                await api('/accounts', { method: 'POST', body: JSON.stringify(accountForm) });
            }
            setShowAccountForm(false);
            setEditingAccountId(null);
            setAccountForm(emptyAccount);
            await loadAll();
            showNotice('ok', 'Cuenta guardada.');
        } catch (error) {
            showNotice('error', error.message);
        } finally {
            setBusy('');
        }
    };

    const setDefaultAccount = async (id) => {
        await api(`/accounts/${id}/default`, { method: 'POST' });
        await loadAll();
    };

    const testAccount = async (id) => {
        const to = window.prompt('Correo para la prueba SMTP (opcional, deja vacío solo para verificar conexión):', testTo || '');
        if (to === null) return;
        setBusy('smtp');
        try {
            const data = await api(`/accounts/${id}/test`, {
                method: 'POST',
                body: JSON.stringify({ to: to.trim() || undefined }),
            });
            showNotice('ok', data.message);
        } catch (error) {
            showNotice('error', error.message);
        } finally {
            setBusy('');
        }
    };

    const deleteAccount = async (id) => {
        if (!window.confirm('¿Eliminar esta cuenta SMTP?')) return;
        await api(`/accounts/${id}`, { method: 'DELETE' });
        await loadAll();
    };

    const insertPlaceholder = (key) => {
        if (!draft) return;
        setDraft({ ...draft, html: `${draft.html}{{${key}}}` });
    };

    return (
        <div style={studio.page}>
            <div style={studio.header}>
                <div>
                    <p style={studio.kicker}>Administración</p>
                    <h2 style={studio.title}>Plantillas de correo</h2>
                    <p style={studio.subtitle}>
                        Activa o apaga cada aviso, edita el HTML y configura remitentes. Las variables van entre llaves, por ejemplo {'{{userName}}'}.
                    </p>
                </div>
                <div style={studio.stats}>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{templates.filter((item) => item.enabled).length}</span>
                        <span style={studio.statLabel}>Activas</span>
                    </div>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{templates.length}</span>
                        <span style={studio.statLabel}>Plantillas</span>
                    </div>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{accounts.length}</span>
                        <span style={studio.statLabel}>Cuentas</span>
                    </div>
                </div>
            </div>

            <StudioTabs
                items={[
                    { id: 'templates', label: 'Plantillas', count: templates.length },
                    { id: 'accounts', label: 'Cuentas y remitentes', count: accounts.length },
                ]}
                value={tab}
                onChange={(next) => { setTab(next); setSelectedId(null); setDraft(null); }}
            />

            {notice && (
                <div style={{
                    ...studio.card,
                    backgroundColor: notice.type === 'error' ? ink.dangerSoft : '#e6f4ea',
                    borderColor: notice.type === 'error' ? '#f6aea9' : '#a8dab5',
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <span>{notice.text}</span>
                        <button type="button" onClick={() => setNotice(null)} style={studio.ghostBtn}>Cerrar</button>
                    </div>
                </div>
            )}

            {tab === 'templates' && !draft && (
                <>
                    <div style={studio.filterDock}>
                        <SearchField value={query} onChange={setQuery} placeholder="Buscar plantilla…" style={{ marginBottom: 0 }} />
                        <div style={{ ...studio.filterPills, marginTop: 12 }}>
                            {categories.map((item) => (
                                <button
                                    key={item}
                                    type="button"
                                    onClick={() => setCategory(item)}
                                    style={{
                                        ...studio.filterChip,
                                        backgroundColor: category === item ? ink.accentSoft : ink.surface,
                                        color: category === item ? ink.accentText : ink.muted,
                                    }}
                                >
                                    {item === 'all' ? 'Todas' : item}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button type="button" onClick={createTemplate} style={studio.primaryBtn} disabled={Boolean(busy)}>
                            {busy === 'create' ? 'Creando…' : 'Nueva plantilla'}
                        </button>
                    </div>
                    {loading ? (
                        <div style={studio.empty}>Cargando plantillas…</div>
                    ) : visible.length === 0 ? (
                        <div style={studio.empty}>Ninguna plantilla coincide.</div>
                    ) : (
                        Object.entries(grouped).map(([group, rows]) => (
                            <div key={group}>
                                <h3 style={studio.groupHead}>{group}</h3>
                                <div style={studio.card}>
                                    <table style={studio.table}>
                                        <thead>
                                            <tr>
                                                <th style={studio.th}>Plantilla</th>
                                                <th style={studio.th}>Enviar</th>
                                                <th style={studio.th}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((item) => (
                                                <tr key={item.id}>
                                                    <td style={studio.td}>
                                                        <div style={{ fontWeight: 600, color: ink.text }}>{item.name}</div>
                                                        <div style={studio.hint}>{item.description || (item.isSystem ? 'Evento del sistema' : 'Personalizada')}</div>
                                                    </td>
                                                    <td style={studio.td}>
                                                        <PermSwitch
                                                            checked={item.enabled}
                                                            onChange={(next) => toggleEnabled(item, next)}
                                                            label={item.enabled ? 'Sí' : 'No'}
                                                        />
                                                    </td>
                                                    <td style={studio.td}>
                                                        <button type="button" onClick={() => openTemplate(item.id)} style={studio.ghostBtn}>Editar HTML</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))
                    )}
                </>
            )}

            {tab === 'templates' && draft && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                        <button type="button" onClick={() => { setDraft(null); setSelectedId(null); }} style={studio.ghostBtn}>‹ Volver al listado</button>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {draft.isSystem && (
                                <button type="button" onClick={resetTemplate} style={studio.ghostBtn} disabled={Boolean(busy)}>Restablecer original</button>
                            )}
                            {!draft.isSystem && (
                                <button type="button" onClick={deleteTemplate} style={studio.dangerBtn} disabled={Boolean(busy)}>Eliminar</button>
                            )}
                            <button type="button" onClick={saveTemplate} style={studio.primaryBtn} disabled={Boolean(busy)}>
                                {busy === 'save' ? 'Guardando…' : 'Guardar'}
                            </button>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div style={studio.card}>
                                <PermSwitch checked={draft.enabled} onChange={(next) => setDraft({ ...draft, enabled: next })} label="Enviar este correo" />
                                <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
                                    <Field label="Nombre">
                                        <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} style={studio.control} />
                                    </Field>
                                    <Field label="Asunto">
                                        <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} style={studio.control} />
                                    </Field>
                                    <Field label="Cuenta remitente">
                                        <select
                                            value={draft.accountId || ''}
                                            onChange={(e) => setDraft({ ...draft, accountId: e.target.value })}
                                            style={studio.control}
                                        >
                                            <option value="">Cuenta por defecto</option>
                                            {accounts.map((account) => (
                                                <option key={account.id} value={account.id}>{account.name}{account.isDefault ? ' · predeterminada' : ''}</option>
                                            ))}
                                        </select>
                                    </Field>
                                </div>
                                {draft.placeholders?.length > 0 && (
                                    <div style={{ marginTop: 14 }}>
                                        <p style={studio.fieldLabel}>Variables</p>
                                        <div style={studio.filterChips}>
                                            {draft.placeholders.map((key) => (
                                                <button key={key} type="button" onClick={() => insertPlaceholder(key)} style={studio.filterChip}>
                                                    {`{{${key}}}`}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <Field label="HTML">
                                    <textarea
                                        value={draft.html}
                                        onChange={(e) => setDraft({ ...draft, html: e.target.value })}
                                        style={{ ...studio.control, minHeight: 320, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: '0.82rem', lineHeight: 1.45 }}
                                    />
                                </Field>
                            </div>
                            <div style={studio.card}>
                                <p style={studio.fieldLabel}>Enviar prueba</p>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end' }}>
                                    <div style={{ flex: 1, minWidth: 220 }}>
                                        <Field label="Destinatario">
                                            <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} style={studio.control} placeholder="tú@centralmedia.mx" />
                                        </Field>
                                    </div>
                                    <button type="button" onClick={sendTest} style={studio.primaryBtn} disabled={Boolean(busy) || !testTo}>
                                        {busy === 'test' ? 'Enviando…' : 'Enviar prueba'}
                                    </button>
                                </div>
                                <p style={{ ...studio.hint, marginTop: 8 }}>Usa los datos de ejemplo de la plantilla. Guarda antes si quieres probar la versión almacenada; si no, se manda el HTML del editor.</p>
                            </div>
                        </div>
                        <div style={studio.card}>
                            <p style={studio.kicker}>Vista previa</p>
                            <h3 style={{ ...studio.formTitle, margin: '6px 0 12px' }}>{livePreview.subject || 'Sin asunto'}</h3>
                            <iframe
                                title="Vista previa del correo"
                                sandbox=""
                                srcDoc={livePreview.html}
                                style={{ width: '100%', minHeight: 560, borderWidth: 1, borderStyle: 'solid', borderColor: ink.line, borderRadius: 12, backgroundColor: '#f4f5f7' }}
                            />
                        </div>
                    </div>
                </>
            )}

            {tab === 'accounts' && (
                <>
                    <div style={studio.card}>
                        <h3 style={{ ...studio.formTitle, marginTop: 0 }}>Marca del correo</h3>
                        <p style={{ ...studio.hint, marginBottom: 16 }}>Logo y URL del portal que se inyectan en {'{{logoUrl}}'} y {'{{portalUrl}}'}.</p>
                        <div style={{ display: 'grid', gridTemplateColumns: cols('1fr 1fr'), gap: 12 }}>
                            <Field label="URL del portal">
                                <input
                                    value={settings.frontendUrl || ''}
                                    onChange={(e) => setSettings({ ...settings, frontendUrl: e.target.value })}
                                    onBlur={() => saveSettings({ frontendUrl: settings.frontendUrl }).catch((error) => showNotice('error', error.message))}
                                    style={studio.control}
                                />
                            </Field>
                            <Field label="URL del logo">
                                <input
                                    value={settings.logoUrl || ''}
                                    onChange={(e) => setSettings({ ...settings, logoUrl: e.target.value })}
                                    onBlur={() => saveSettings({ logoUrl: settings.logoUrl }).catch((error) => showNotice('error', error.message))}
                                    style={studio.control}
                                />
                            </Field>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                            type="button"
                            style={studio.primaryBtn}
                            onClick={() => {
                                setEditingAccountId(null);
                                setAccountForm(emptyAccount);
                                setShowAccountForm(true);
                            }}
                        >
                            Nueva cuenta SMTP
                        </button>
                    </div>

                    {showAccountForm && (
                        <form onSubmit={saveAccount} style={studio.formPanel}>
                            <h4 style={studio.formTitle}>{editingAccountId ? 'Editar cuenta' : 'Nueva cuenta'}</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: cols('1fr 1fr'), gap: 12 }}>
                                <Field label="Nombre interno">
                                    <input required value={accountForm.name} onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })} style={studio.control} />
                                </Field>
                                <Field label="Nombre del remitente">
                                    <input value={accountForm.fromName} onChange={(e) => setAccountForm({ ...accountForm, fromName: e.target.value })} style={studio.control} />
                                </Field>
                                <Field label="Correo remitente">
                                    <input required type="email" value={accountForm.fromEmail} onChange={(e) => setAccountForm({ ...accountForm, fromEmail: e.target.value })} style={studio.control} />
                                </Field>
                                <Field label="Servidor SMTP">
                                    <input required={editingAccountId !== 'env'} value={accountForm.host} onChange={(e) => setAccountForm({ ...accountForm, host: e.target.value })} style={studio.control} disabled={editingAccountId === 'env'} />
                                </Field>
                                <Field label="Puerto">
                                    <input type="number" value={accountForm.port} onChange={(e) => setAccountForm({ ...accountForm, port: Number(e.target.value) })} style={studio.control} disabled={editingAccountId === 'env'} />
                                </Field>
                                <Field label="Usuario SMTP">
                                    <input value={accountForm.user} onChange={(e) => setAccountForm({ ...accountForm, user: e.target.value })} style={studio.control} disabled={editingAccountId === 'env'} />
                                </Field>
                                <Field label="Contraseña" span={2}>
                                    <input type="password" value={accountForm.password} onChange={(e) => setAccountForm({ ...accountForm, password: e.target.value })} style={studio.control} placeholder={editingAccountId ? 'Dejar vacío para no cambiar' : ''} disabled={editingAccountId === 'env'} />
                                </Field>
                            </div>
                            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, color: ink.text }}>
                                <input type="checkbox" checked={accountForm.secure} onChange={(e) => setAccountForm({ ...accountForm, secure: e.target.checked })} disabled={editingAccountId === 'env'} />
                                Conexión segura (puerto 465)
                            </label>
                            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, color: ink.text }}>
                                <input type="checkbox" checked={accountForm.makeDefault} onChange={(e) => setAccountForm({ ...accountForm, makeDefault: e.target.checked })} />
                                Usar como cuenta predeterminada
                            </label>
                            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                                <button type="submit" style={studio.primaryBtn} disabled={Boolean(busy)}>{busy === 'account' ? 'Guardando…' : 'Guardar cuenta'}</button>
                                <button type="button" style={studio.ghostBtn} onClick={() => { setShowAccountForm(false); setEditingAccountId(null); }}>Cancelar</button>
                            </div>
                        </form>
                    )}

                    {accounts.map((account) => (
                        <div key={account.id} style={studio.card}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                                <div>
                                    <h4 style={{ margin: '0 0 4px', color: ink.text }}>{account.name}</h4>
                                    <p style={studio.hint}>
                                        {account.fromName} &lt;{account.fromEmail || 'sin remitente'}&gt;
                                        {account.host ? ` · ${account.host}:${account.port}` : ' · SMTP del servidor'}
                                        {account.isDefault ? ' · Predeterminada' : ''}
                                        {account.isEnv ? ' · Variables de entorno' : ''}
                                    </p>
                                </div>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {!account.isDefault && (
                                        <button type="button" style={studio.ghostBtn} onClick={() => setDefaultAccount(account.id)}>Predeterminada</button>
                                    )}
                                    <button type="button" style={studio.ghostBtn} onClick={() => testAccount(account.id)} disabled={Boolean(busy)}>Probar</button>
                                    <button
                                        type="button"
                                        style={studio.ghostBtn}
                                        onClick={() => {
                                            setEditingAccountId(account.id);
                                            setAccountForm({
                                                name: account.name,
                                                fromName: account.fromName,
                                                fromEmail: account.fromEmail,
                                                host: account.host,
                                                port: account.port,
                                                secure: account.secure,
                                                user: account.user,
                                                password: '',
                                                makeDefault: account.isDefault,
                                            });
                                            setShowAccountForm(true);
                                        }}
                                    >
                                        Editar
                                    </button>
                                    {!account.isEnv && (
                                        <button type="button" style={studio.dangerBtn} onClick={() => deleteAccount(account.id)}>Eliminar</button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
};

export default EmailTemplatesPanel;
